import { useState, useEffect } from 'react';
import { X, Save, FileText } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface CreateTaxReceiptModalProps {
  associationId: string;
  associationName: string;
  userId: string;
  existingReceipt?: any;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateTaxReceiptModal({
  associationId,
  associationName,
  userId,
  existingReceipt,
  onClose,
  onSuccess
}: CreateTaxReceiptModalProps) {
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [formData, setFormData] = useState({
    donor_type: existingReceipt?.donor_type || 'individual',
    donor_name: existingReceipt?.donor_name || '',
    donor_company_name: existingReceipt?.donor_company_name || '',
    donor_legal_form: existingReceipt?.donor_legal_form || '',
    donor_siren: existingReceipt?.donor_siren || '',
    donor_address_number: existingReceipt?.donor_address_number || '',
    donor_address_street: existingReceipt?.donor_address_street || '',
    donor_postal_code: existingReceipt?.donor_postal_code || '',
    donor_city: existingReceipt?.donor_city || '',
    donor_country: existingReceipt?.donor_country || 'France',
    donation_date: existingReceipt?.donation_date || new Date().toISOString().split('T')[0],
    donation_amount: existingReceipt?.donation_amount || '',
    donation_nature_amount: existingReceipt?.donation_nature_amount || '',
    donation_nature_description: existingReceipt?.donation_nature_description || '',
    donation_payment_method: existingReceipt?.donation_payment_method || 'transfer',
    organization_type: existingReceipt?.organization_type || 'association_loi_1901',
    organization_subtype: existingReceipt?.organization_subtype || '',
    organization_utility_public_date: existingReceipt?.organization_utility_public_date || '',
    organization_accreditation_date: existingReceipt?.organization_accreditation_date || '',
    organization_other_details: existingReceipt?.organization_other_details || '',
    issued_by: existingReceipt?.issued_by || '',
    signature_position: existingReceipt?.signature_position || '',
    notes: existingReceipt?.notes || ''
  });

  async function handleSaveDraft() {
    try {
      setSaving(true);

      const receiptData = {
        association_id: associationId,
        user_id: userId,
        status: 'draft',
        donor_type: formData.donor_type,
        donor_name: formData.donor_name,
        donor_company_name: formData.donor_company_name || null,
        donor_legal_form: formData.donor_legal_form || null,
        donor_siren: formData.donor_siren || null,
        donor_address_number: formData.donor_address_number || null,
        donor_address_street: formData.donor_address_street,
        donor_postal_code: formData.donor_postal_code,
        donor_city: formData.donor_city,
        donor_country: formData.donor_country || 'France',
        donation_date: formData.donation_date,
        donation_amount: Number(formData.donation_amount) || 0,
        donation_nature_amount: Number(formData.donation_nature_amount) || 0,
        donation_nature_description: formData.donation_nature_description || null,
        donation_payment_method: formData.donation_payment_method || null,
        organization_type: formData.organization_type,
        organization_subtype: formData.organization_subtype || null,
        organization_utility_public_date: formData.organization_utility_public_date || null,
        organization_accreditation_date: formData.organization_accreditation_date || null,
        organization_other_details: formData.organization_other_details || null,
        issued_by: formData.issued_by || null,
        signature_position: formData.signature_position || null,
        notes: formData.notes || null
      };

      if (existingReceipt?.id) {
        const { error } = await supabase
          .from('tax_receipts')
          .update(receiptData)
          .eq('id', existingReceipt.id);

        if (error) throw error;
      } else {
        const receiptNumber = await generateReceiptNumber();
        const { error } = await supabase
          .from('tax_receipts')
          .insert({ ...receiptData, receipt_number: receiptNumber });

        if (error) throw error;
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Erreur lors de l\'enregistrement du brouillon');
    } finally {
      setSaving(false);
    }
  }

  async function generateReceiptNumber(): Promise<string> {
    const { data, error } = await supabase.rpc('generate_receipt_number', {
      p_association_id: associationId
    });

    if (error) throw error;
    return data;
  }

  async function handleGeneratePDF() {
    try {
      setGenerating(true);

      let receiptId = existingReceipt?.id;

      if (!receiptId) {
        const receiptNumber = await generateReceiptNumber();
        const receiptData = {
          association_id: associationId,
          user_id: userId,
          receipt_number: receiptNumber,
          status: 'draft',
          donor_type: formData.donor_type,
          donor_name: formData.donor_name,
          donor_company_name: formData.donor_company_name || null,
          donor_legal_form: formData.donor_legal_form || null,
          donor_siren: formData.donor_siren || null,
          donor_address_number: formData.donor_address_number || null,
          donor_address_street: formData.donor_address_street,
          donor_postal_code: formData.donor_postal_code,
          donor_city: formData.donor_city,
          donor_country: formData.donor_country || 'France',
          donation_date: formData.donation_date,
          donation_amount: Number(formData.donation_amount) || 0,
          donation_nature_amount: Number(formData.donation_nature_amount) || 0,
          donation_nature_description: formData.donation_nature_description || null,
          donation_payment_method: formData.donation_payment_method || null,
          organization_type: formData.organization_type,
          organization_subtype: formData.organization_subtype || null,
          organization_utility_public_date: formData.organization_utility_public_date || null,
          organization_accreditation_date: formData.organization_accreditation_date || null,
          organization_other_details: formData.organization_other_details || null,
          issued_by: formData.issued_by || null,
          signature_position: formData.signature_position || null,
          notes: formData.notes || null
        };

        const { data: newReceipt, error: insertError } = await supabase
          .from('tax_receipts')
          .insert(receiptData)
          .select()
          .single();

        if (insertError) throw insertError;
        receiptId = newReceipt.id;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-tax-receipt-pdf`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ receipt_id: receiptId })
        }
      );

      const result = await response.json();
      console.log('PDF Generation Response:', result);

      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors de la génération du PDF');
      }

      if (!result.success) {
        throw new Error(result.error || 'Erreur inconnue lors de la génération du PDF');
      }

      await supabase
        .from('tax_receipts')
        .update({
          status: 'issued',
          issue_date: new Date().toISOString().split('T')[0],
          pdf_url: result.pdf_url
        })
        .eq('id', receiptId);

      alert('Reçu fiscal généré avec succès !');
      onSuccess();
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(`Erreur lors de la génération du PDF: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setGenerating(false);
    }
  }

  const organizationTypes = [
    { value: 'association_loi_1901', label: 'Association loi 1901' },
    { value: 'association_utilite_publique', label: 'Association reconnue d\'utilité publique' },
    { value: 'fondation_entreprise', label: 'Fondation d\'entreprise' },
    { value: 'association_cultuelle', label: 'Association cultuelle' },
    { value: 'fonds_dotation', label: 'Fonds de dotation' },
    { value: 'other', label: 'Autre' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {existingReceipt?.id ? 'Modifier' : 'Nouveau'} reçu fiscal
            </h3>
            <p className="text-sm text-gray-600 mt-1">{associationName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>CERFA 16216*02 :</strong> Reçu des dons et versements effectués par les entreprises au titre de l'article 238 bis du code général des impôts
            </p>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">A. Type de donateur</h4>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="donor_type"
                  value="individual"
                  checked={formData.donor_type === 'individual'}
                  onChange={(e) => setFormData({ ...formData, donor_type: e.target.value as 'individual' | 'company' })}
                  className="w-4 h-4 text-blue-600"
                />
                <span>Particulier</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="donor_type"
                  value="company"
                  checked={formData.donor_type === 'company'}
                  onChange={(e) => setFormData({ ...formData, donor_type: e.target.value as 'individual' | 'company' })}
                  className="w-4 h-4 text-blue-600"
                />
                <span>Entreprise</span>
              </label>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">B. Informations donateur</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du donateur *
                </label>
                <input
                  type="text"
                  value={formData.donor_name}
                  onChange={(e) => setFormData({ ...formData, donor_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {formData.donor_type === 'company' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dénomination entreprise
                    </label>
                    <input
                      type="text"
                      value={formData.donor_company_name}
                      onChange={(e) => setFormData({ ...formData, donor_company_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Forme juridique
                    </label>
                    <input
                      type="text"
                      value={formData.donor_legal_form}
                      onChange={(e) => setFormData({ ...formData, donor_legal_form: e.target.value })}
                      placeholder="SARL, SAS, SA..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Numéro SIREN
                    </label>
                    <input
                      type="text"
                      value={formData.donor_siren}
                      onChange={(e) => setFormData({ ...formData, donor_siren: e.target.value })}
                      placeholder="9 chiffres"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  N° *
                </label>
                <input
                  type="text"
                  value={formData.donor_address_number}
                  onChange={(e) => setFormData({ ...formData, donor_address_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rue *
                </label>
                <input
                  type="text"
                  value={formData.donor_address_street}
                  onChange={(e) => setFormData({ ...formData, donor_address_street: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code postal *
                </label>
                <input
                  type="text"
                  value={formData.donor_postal_code}
                  onChange={(e) => setFormData({ ...formData, donor_postal_code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Commune *
                </label>
                <input
                  type="text"
                  value={formData.donor_city}
                  onChange={(e) => setFormData({ ...formData, donor_city: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pays
                </label>
                <input
                  type="text"
                  value={formData.donor_country}
                  onChange={(e) => setFormData({ ...formData, donor_country: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">C. Don</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date du don *
                </label>
                <input
                  type="date"
                  value={formData.donation_date}
                  onChange={(e) => setFormData({ ...formData, donation_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Montant (versements) *
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.donation_amount}
                    onChange={(e) => setFormData({ ...formData, donation_amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">€</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Montant dons en nature
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.donation_nature_amount}
                    onChange={(e) => setFormData({ ...formData, donation_nature_amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">€</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Forme des versements
                </label>
                <select
                  value={formData.donation_payment_method}
                  onChange={(e) => setFormData({ ...formData, donation_payment_method: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="cash">Remise d'espèces</option>
                  <option value="check">Chèque</option>
                  <option value="transfer">Virement, prélèvement ou carte bancaire</option>
                  <option value="other">Autre</option>
                </select>
              </div>
            </div>

            {Number(formData.donation_nature_amount) > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description des biens et prestations reçus
                </label>
                <textarea
                  value={formData.donation_nature_description}
                  onChange={(e) => setFormData({ ...formData, donation_nature_description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Nature et quantité des biens, détail des prestations..."
                />
              </div>
            )}
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">D. Type d'organisme bénéficiaire</h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type d'organisme *
              </label>
              <select
                value={formData.organization_type}
                onChange={(e) => setFormData({ ...formData, organization_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                {organizationTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            {formData.organization_type === 'other' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Précisez
                </label>
                <input
                  type="text"
                  value={formData.organization_other_details}
                  onChange={(e) => setFormData({ ...formData, organization_other_details: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">E. Attestation & signature</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du signataire
                </label>
                <input
                  type="text"
                  value={formData.issued_by}
                  onChange={(e) => setFormData({ ...formData, issued_by: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Président, Trésorier..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fonction
                </label>
                <input
                  type="text"
                  value={formData.signature_position}
                  onChange={(e) => setFormData({ ...formData, signature_position: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Président, Trésorier..."
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes internes (non imprimées)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Notes, remarques..."
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSaveDraft}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Enregistrement...' : 'Enregistrer brouillon'}
          </button>
          <button
            onClick={handleGeneratePDF}
            disabled={generating || !formData.donor_name || !formData.donation_amount}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <FileText className="w-4 h-4" />
            {generating ? 'Génération...' : 'Générer PDF & Émettre'}
          </button>
        </div>
      </div>
    </div>
  );
}
